// components/telefonie/TelefonieKarte.tsx
// Verbinden-Karte im Profil: Webex-Konto verbinden/trennen. Treibt istTelefonieVerbunden()
// (lib/telefonie/status.ts), das TelefonLink (components/ui/TelefonLink.tsx) für den
// Wähl-Knopf abfragt.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, CircleNotch, Phone, PhoneDisconnect } from '@phosphor-icons/react';
import { supabase, supabaseUrl, supabaseAnonKey } from '../../lib/supabase/client';
import { verwirfTelefonieStatus } from '../../lib/telefonie/status';
import { useNotification } from '../../contexts/NotificationContext';

interface StatusZeile {
  verbunden: boolean;
  seit: string | null;
}

// Rückmeldung nach der Rückkehr von Webex, siehe telefonie-oauth: die Function leitet
// auf /admin/profile?telefonie=verbunden|fehler|abgebrochen.
const RUECKMELDUNG: Record<string, { typ: 'success' | 'error'; text: string }> = {
  verbunden: { typ: 'success', text: 'Webex-Konto erfolgreich verbunden.' },
  fehler: { typ: 'error', text: 'Verbinden fehlgeschlagen. Bitte erneut versuchen.' },
  abgebrochen: { typ: 'error', text: 'Verbinden wurde abgebrochen.' },
};

const meldungAus = (err: unknown): string => (err as { message?: string } | null)?.message ?? '';

async function holeZugangstoken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Keine aktive Sitzung.');
  return session.access_token;
}

export default function TelefonieKarte() {
  const { showToast } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<StatusZeile | null>(null);
  const [ladeStatus, setLadeStatus] = useState(true);
  const [aktion, setAktion] = useState<'verbinden' | 'trennen' | null>(null);

  const ladeStatusNeu = async () => {
    setLadeStatus(true);
    const { data } = await supabase
      .from('v_telefonie_benutzer_status')
      .select('verbunden_am, ist_gueltig')
      .maybeSingle();
    setStatus({ verbunden: !!data?.ist_gueltig, seit: data?.verbunden_am ?? null });
    setLadeStatus(false);
  };

  useEffect(() => { void ladeStatusNeu(); }, []);

  // Rückweg von Webex auswerten: Rückmeldung zeigen, Parameter wegräumen, Status neu laden.
  useEffect(() => {
    const ergebnis = searchParams.get('telefonie');
    if (!ergebnis) return;

    const meldung = RUECKMELDUNG[ergebnis];
    if (meldung) showToast({ type: meldung.typ, message: meldung.text, modul: 'system' });

    const naechste = new URLSearchParams(searchParams);
    naechste.delete('telefonie');
    setSearchParams(naechste, { replace: true });

    verwirfTelefonieStatus();
    void ladeStatusNeu();
    // Nur beim Wechsel des Parameters selbst reagieren — showToast/setSearchParams
    // bleiben über Renders stabil, ladeStatusNeu braucht keine Dependency-Deklaration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const verbinden = async () => {
    setAktion('verbinden');
    try {
      const token = await holeZugangstoken();
      const res = await fetch(`${supabaseUrl}/functions/v1/telefonie-oauth?start=1`, {
        headers: { Authorization: `Bearer ${token}`, apikey: supabaseAnonKey },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.url) throw new Error(body?.error || 'Webex-Anmeldung fehlgeschlagen.');
      window.location.href = body.url;
    } catch (err: unknown) {
      showToast({ type: 'error', message: meldungAus(err) || 'Webex-Verbindung fehlgeschlagen.', modul: 'system' });
      setAktion(null);
    }
  };

  const trennen = async () => {
    setAktion('trennen');
    try {
      const token = await holeZugangstoken();
      const res = await fetch(`${supabaseUrl}/functions/v1/telefonie-oauth?trennen=1`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, apikey: supabaseAnonKey },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.ok) throw new Error(body?.error || 'Trennen fehlgeschlagen.');
      verwirfTelefonieStatus();
      showToast({ type: 'success', message: 'Webex-Konto getrennt.', modul: 'system' });
      await ladeStatusNeu();
    } catch (err: unknown) {
      showToast({ type: 'error', message: meldungAus(err) || 'Trennen fehlgeschlagen.', modul: 'system' });
    } finally {
      setAktion(null);
    }
  };

  const verbunden = !ladeStatus && !!status?.verbunden;
  const knopf = { height: 44, padding: '0 20px', gap: 8, borderRadius: 22, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
    cursor: aktion ? 'wait' : 'pointer', opacity: aktion ? 0.6 : 1, transition: 'background .2s, color .2s' } as const;

  // Darstellung nach design_handoff_profil §3 – Logik oben unverändert.
  return (
    <section aria-label="Telefonie" className="immo-glas flex flex-wrap items-center"
      style={{ gridColumn: '1 / -1', borderRadius: 28, padding: '20px 22px', gap: 16 }}>
      <span className="flex shrink-0 items-center justify-center"
        style={{ width: 52, height: 52, borderRadius: 16, transition: 'all .3s',
          ...(verbunden
            ? { background: 'linear-gradient(160deg, color-mix(in srgb, var(--ok) 65%, #fff), var(--ok))', color: '#fff', boxShadow: '0 12px 24px -12px var(--ok)' }
            : { background: 'var(--fill2)', color: 'var(--acc)' }) }}>
        <Phone size={25} weight="fill" />
      </span>
      <div className="flex min-w-0 flex-col" style={{ flex: '1 1 260px', gap: 4 }}>
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <h2 className="m-0" style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em' }}>Telefonie</h2>
          {verbunden && (
            <span className="inline-flex items-center" style={{ height: 22, padding: '0 10px', gap: 6, borderRadius: 11, fontSize: 11.5, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
              <CheckCircle size={13} weight="fill" />Webex-Konto
            </span>
          )}
        </div>
        {ladeStatus ? (
          <span className="flex items-center" style={{ gap: 8, fontSize: 14, color: 'var(--ink3)' }}>
            <CircleNotch size={16} weight="bold" className="animate-spin" />Status wird geladen …
          </span>
        ) : verbunden ? (
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>
            Verbunden seit {status?.seit ? new Date(status.seit).toLocaleDateString('de-DE') : '–'}
          </span>
        ) : (
          <span style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--ink2)' }}>
            Verbinde dein Webex-Konto, um aus der App heraus zu telefonieren.
          </span>
        )}
      </div>
      {!ladeStatus && (verbunden ? (
        <button type="button" onClick={() => void trennen()} disabled={aktion !== null}
          data-tip="Trennen – Webex-Konto von deinem Profil lösen. Telefonieren aus der App ist dann nicht mehr möglich."
          className="inline-flex shrink-0 items-center hover:!bg-[var(--bad-soft)] hover:!text-[var(--bad)]"
          style={{ ...knopf, background: 'var(--fill2)', color: 'var(--ink)' }}>
          {aktion === 'trennen' ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <PhoneDisconnect size={16} weight="bold" />}
          Trennen
        </button>
      ) : (
        <button type="button" onClick={() => void verbinden()} disabled={aktion !== null}
          data-tip="Webex verbinden – Weiterleitung zur Webex-Anmeldung, danach zurück ins Profil"
          className="inline-flex shrink-0 items-center"
          style={{ ...knopf, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
          {aktion === 'verbinden' ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Phone size={16} weight="bold" />}
          Webex verbinden
        </button>
      ))}
    </section>
  );
}
